import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  UserPlus,
  Files,
  History,
  Settings,
  PhoneCall,
  Menu,
  X,
} from 'lucide-react';
import { APP_VERSION } from '../services/env';

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Single Entry', href: '/single', icon: UserPlus },
    { name: 'Bulk Upload', href: '/bulk', icon: Files },
    { name: 'Call History', href: '/history', icon: History },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/' || location.pathname === '';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const title =
    location.pathname === '/' || location.pathname === ''
      ? 'Overview'
      : (location.pathname.split('/').filter(Boolean)[0] || 'overview').replace(/-/g, ' ');

  const NavLinks = ({ onNavigate }: { onNavigate?: () => void }) => (
    <>
      {navigation.map((item) => {
        const active = isActive(item.href);
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={onNavigate}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
              active
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/20'
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <item.icon className="w-5 h-5 shrink-0" />
            <span className="font-medium">{item.name}</span>
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden transition-colors duration-300">
      <aside className="hidden md:flex flex-col w-64 bg-slate-900 dark:bg-black text-white border-r border-slate-800 transition-colors duration-300">
        <div className="p-6 flex items-center space-x-3">
          <div className="bg-indigo-600 p-2 rounded-lg">
            <PhoneCall className="w-6 h-6" />
          </div>
          <span className="text-xl font-bold tracking-tight">ReachAI</span>
        </div>

        <nav className="flex-1 mt-6 px-4 space-y-1">
          <NavLinks />
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="p-3 rounded-xl bg-slate-800/50 text-center">
            <div className="text-xs text-slate-400">Protected by Cloudflare Zero Trust</div>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed z-50 top-0 left-0 h-full w-72 bg-slate-900 text-white border-r border-slate-800 flex flex-col md:hidden transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <PhoneCall className="w-6 h-6" />
            </div>
            <span className="text-lg font-bold">ReachAI</span>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavLinks onNavigate={() => setMobileOpen(false)} />
        </nav>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-auto">
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-30 transition-colors duration-300">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100 capitalize truncate">
              {title}
            </h1>
          </div>
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded shrink-0">
            v{APP_VERSION}
          </div>
        </header>

        <div className="p-4 sm:p-8 flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
